'use client';

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Repeat,
  Calendar,
  Clock,
  Activity,
  Users,
  MapPin,
  User,
  Briefcase,
  AlertTriangle,
  CheckCircle2,
  PauseCircle,
  PlayCircle,
  Timer,
  ArrowUpRight,
  ClipboardList,
  Shield,
  Tags,
  Save,
  Loader2,
  X,
  Zap,
  FlaskConical,
} from 'lucide-react';
import type { WorkItem } from '../UniversalWorkItems';
import { authService } from '@/lib/authService';
import { notify, confirm } from '@/lib/notifications';

interface RecurringJobOverviewTabProps {
  workItem: WorkItem;
  formData: Record<string, unknown>;
  isDark?: boolean;
  onRefresh?: () => void;
}

interface RecurringJobEditData {
  frequency: string;
  interval_count: number | string;
  day_of_week: number | string;
  day_of_month: number | string;
  month_of_year: number | string;
  generate_time: string;
  timezone: string;
  start_date: string;
  end_date: string;
  end_after_occurrences: number | string;
  due_days_after_creation: number;
  overdue_days: number;
  escalation_days: number;
  auto_assign: boolean;
  auto_approve: boolean;
  create_appointments: boolean;
  notes: string;
  internal_notes: string;
}

const FREQUENCY_OPTIONS = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'fortnightly', label: 'Fortnightly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'biannually', label: 'Bi-annually' },
  { value: 'annually', label: 'Annually' },
];

const TIMEZONE_OPTIONS = [
  'Africa/Johannesburg',
  'Africa/Cairo',
  'Africa/Lagos',
  'Africa/Nairobi',
  'Europe/London',
  'Europe/Paris',
  'America/New_York',
  'America/Chicago',
  'America/Los_Angeles',
  'Asia/Dubai',
  'Asia/Singapore',
  'Australia/Sydney',
  'UTC',
];

const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function formatSubtypeLabel(value?: string | null) {
  return String(value || '')
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatDate(value?: string | null) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatDateTime(value?: string | null) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatBoolean(value?: boolean | null, trueLabel = 'Enabled', falseLabel = 'Disabled') {
  return value ? trueLabel : falseLabel;
}

function getFrequencyLabel(customFields: Record<string, unknown>, fallbackSubtype?: string | null) {
  if (typeof customFields.frequency_label === 'string' && customFields.frequency_label.trim()) {
    return customFields.frequency_label.trim();
  }

  const frequency = String(customFields.frequency || fallbackSubtype || '').trim().toLowerCase();
  if (!frequency) {
    return 'Recurring schedule';
  }

  const intervalCount = Number(customFields.interval_count || 1);
  const dayOfWeek = Number.isFinite(Number(customFields.day_of_week)) ? Number(customFields.day_of_week) : null;
  const dayOfMonth = Number.isFinite(Number(customFields.day_of_month)) ? Number(customFields.day_of_month) : null;
  const monthOfYear = Number.isFinite(Number(customFields.month_of_year)) ? Number(customFields.month_of_year) : null;

  const baseLabel = (() => {
    switch (frequency) {
      case 'daily':
        return intervalCount === 1 ? 'Daily' : `Every ${intervalCount} days`;
      case 'weekly':
        return intervalCount === 1 ? 'Weekly' : `Every ${intervalCount} weeks`;
      case 'fortnightly':
        return 'Fortnightly';
      case 'monthly':
        return intervalCount === 1 ? 'Monthly' : `Every ${intervalCount} months`;
      case 'quarterly':
        return 'Quarterly';
      case 'biannually':
        return 'Bi-annually';
      case 'annually':
        return 'Annually';
      default:
        return formatSubtypeLabel(frequency);
    }
  })();

  if (frequency === 'weekly' && dayOfWeek !== null && dayOfWeek >= 0 && dayOfWeek <= 6) {
    return `${baseLabel} on ${DAYS_OF_WEEK[dayOfWeek]}`;
  }

  if (['monthly', 'quarterly', 'biannually'].includes(frequency) && dayOfMonth !== null && dayOfMonth > 0) {
    const suffix = dayOfMonth >= 11 && dayOfMonth <= 13
      ? 'th'
      : ({ 1: 'st', 2: 'nd', 3: 'rd' }[dayOfMonth % 10] || 'th');
    return `${baseLabel} on the ${dayOfMonth}${suffix}`;
  }

  if (frequency === 'annually' && monthOfYear !== null && monthOfYear >= 1 && monthOfYear <= 12) {
    return `${baseLabel} in ${MONTHS[monthOfYear - 1]}`;
  }

  return baseLabel;
}

function StatusBadge({ isActive, isPaused }: { isActive?: boolean; isPaused?: boolean }) {
  if (isPaused) {
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
        <PauseCircle className="w-3.5 h-3.5" />
        Paused
      </span>
    );
  }

  if (isActive === false) {
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300">
        <Activity className="w-3.5 h-3.5" />
        Inactive
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
      <PlayCircle className="w-3.5 h-3.5" />
      Active
    </span>
  );
}

function InfoCard({
  icon: Icon,
  label,
  value,
  helper,
  accentClassName,
  isDark,
}: {
  icon: React.ElementType;
  label: string;
  value: React.ReactNode;
  helper?: React.ReactNode;
  accentClassName?: string;
  isDark?: boolean;
}) {
  return (
    <div className={`rounded-xl border p-4 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className={`text-xs uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{label}</p>
          <div className={`mt-2 text-lg font-semibold ${accentClassName || (isDark ? 'text-white' : 'text-gray-900')}`}>{value}</div>
          {helper && <p className={`mt-1 text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{helper}</p>}
        </div>
        <Icon className={`w-5 h-5 ${accentClassName || 'text-violet-500'}`} />
      </div>
    </div>
  );
}

function DetailRow({
  label,
  value,
  icon: Icon,
  isDark,
}: {
  label: string;
  value: React.ReactNode;
  icon?: React.ElementType;
  isDark?: boolean;
}) {
  return (
    <div className="flex items-start justify-between gap-4 py-2.5">
      <div className={`flex items-center gap-2 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
        {Icon ? <Icon className="w-4 h-4" /> : null}
        <span>{label}</span>
      </div>
      <div className={`text-sm font-medium text-right ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{value || '—'}</div>
    </div>
  );
}

export default function RecurringJobOverviewTab({ workItem, formData, isDark = false, onRefresh }: RecurringJobOverviewTabProps) {
  const customFields = useMemo(() => {
    const workItemFields = workItem.custom_fields && typeof workItem.custom_fields === 'object' ? workItem.custom_fields : {};
    const formFields = formData.custom_fields && typeof formData.custom_fields === 'object' ? formData.custom_fields : {};
    return {
      ...workItemFields,
      ...formFields,
    } as Record<string, unknown>;
  }, [formData.custom_fields, workItem.custom_fields]);

  const scheduleId = String(workItem.id || '');

  // ---- Editable state ----
  const [editData, setEditData] = useState<RecurringJobEditData>({} as RecurringJobEditData);
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  // Initialize edit state from customFields
  useEffect(() => {
    setEditData({
      frequency: String(customFields.frequency || 'monthly'),
      interval_count: Number(customFields.interval_count || 1),
      day_of_week: (customFields.day_of_week as string | number | null) ?? '',
      day_of_month: (customFields.day_of_month as string | number | null) ?? '',
      month_of_year: (customFields.month_of_year as string | number | null) ?? '',
      generate_time: String(customFields.generate_time || '06:00'),
      timezone: String(customFields.timezone || 'Africa/Johannesburg'),
      start_date: String(customFields.start_date || workItem.start_date || '').split('T')[0],
      end_date: String(customFields.end_date || '').split('T')[0],
      end_after_occurrences: (customFields.end_after_occurrences as string | number | null) ?? '',
      due_days_after_creation: Number(customFields.due_days_after_creation ?? 7),
      overdue_days: Number(customFields.overdue_days ?? 3),
      escalation_days: Number(customFields.escalation_days ?? 5),
      auto_assign: Boolean(customFields.auto_assign),
      auto_approve: customFields.auto_approve !== false,
      create_appointments: Boolean(customFields.create_appointments),
      notes: String(customFields.notes || (workItem as Record<string, unknown>).notes || formData.notes || ''),
      internal_notes: String(customFields.internal_notes || (workItem as Record<string, unknown>).internal_notes || formData.internal_notes || ''),
    });
    setIsDirty(false);
  }, [customFields, workItem, formData.notes, formData.internal_notes]);

  const handleChange = useCallback((field: keyof RecurringJobEditData, value: string | number | boolean) => {
    setEditData(prev => {
      const next = { ...prev, [field]: value };
      setIsDirty(true);
      return next;
    });
  }, []);

  const handleSave = useCallback(async () => {
    if (!scheduleId) {
      notify.error('Cannot save: schedule ID not found');
      return;
    }
    setSaving(true);
    try {
      const token = authService.getTokens()?.access_token;
      if (!token) throw new Error('Not authenticated');

      const payload: Record<string, string | number | boolean | undefined> = {
        action: 'update',
        id: scheduleId,
        frequency: editData.frequency,
        interval_count: Number(editData.interval_count) || 1,
        generate_time: editData.generate_time,
        timezone: editData.timezone,
        start_date: editData.start_date || undefined,
        end_date: editData.end_date || undefined,
        end_after_occurrences: editData.end_after_occurrences !== '' ? Number(editData.end_after_occurrences) : undefined,
        due_days_after_creation: Number(editData.due_days_after_creation) || 7,
        overdue_days: Number(editData.overdue_days) ?? 3,
        escalation_days: Number(editData.escalation_days) ?? 5,
        auto_assign: Boolean(editData.auto_assign),
        auto_approve: Boolean(editData.auto_approve),
        create_appointments: Boolean(editData.create_appointments),
        notes: editData.notes || undefined,
        internal_notes: editData.internal_notes || undefined,
      };

      if (editData.day_of_week !== '' && editData.day_of_week != null) {
        payload.day_of_week = Number(editData.day_of_week);
      }
      if (editData.day_of_month !== '' && editData.day_of_month != null) {
        payload.day_of_month = Number(editData.day_of_month);
      }
      if (editData.month_of_year !== '' && editData.month_of_year != null) {
        payload.month_of_year = Number(editData.month_of_year);
      }

      const response = await fetch('/api/v1/work-items/recurring', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to save recurring job schedule');
      }

      notify.success('Recurring job schedule updated');
      setIsDirty(false);
      onRefresh?.();
    } catch (err: unknown) {
      console.error('Save recurring job error:', err);
      notify.error(err instanceof Error ? err.message : 'Failed to save schedule');
    } finally {
      setSaving(false);
    }
  }, [scheduleId, editData, onRefresh]);

  const handleReset = useCallback(() => {
    setEditData(prev => {
      const fresh: RecurringJobEditData = {
        frequency: String(customFields.frequency || 'monthly'),
        interval_count: Number(customFields.interval_count || 1),
        day_of_week: (customFields.day_of_week as string | number | null) ?? '',
        day_of_month: (customFields.day_of_month as string | number | null) ?? '',
        month_of_year: (customFields.month_of_year as string | number | null) ?? '',
        generate_time: String(customFields.generate_time || '06:00'),
        timezone: String(customFields.timezone || 'Africa/Johannesburg'),
        start_date: String(customFields.start_date || workItem.start_date || '').split('T')[0],
        end_date: String(customFields.end_date || '').split('T')[0],
        end_after_occurrences: (customFields.end_after_occurrences as string | number | null) ?? '',
        due_days_after_creation: Number(customFields.due_days_after_creation ?? 7),
        overdue_days: Number(customFields.overdue_days ?? 3),
        escalation_days: Number(customFields.escalation_days ?? 5),
        auto_assign: Boolean(customFields.auto_assign),
        auto_approve: customFields.auto_approve !== false,
        create_appointments: Boolean(customFields.create_appointments),
        notes: String(customFields.notes || (workItem as Record<string, unknown>).notes || formData.notes || ''),
        internal_notes: String(customFields.internal_notes || (workItem as Record<string, unknown>).internal_notes || formData.internal_notes || ''),
      };
      return fresh;
    });
    setIsDirty(false);
  }, [customFields, workItem, formData.notes, formData.internal_notes]);

  // ---- Display values (from editData when dirty, otherwise from customFields) ----
  const title = String(formData.title || workItem.title || 'Recurring Job Schedule');
  const description = String(formData.description || workItem.description || customFields.notes || '').trim();
  const frequencyLabel = getFrequencyLabel(customFields, (formData.item_subtype as string | undefined) || workItem.item_subtype);
  const nextGenerationDate = (customFields.next_generation_date as string | null) || workItem.due_date || null;
  const lastGeneratedAt = (customFields.last_generated_at as string | null) || null;
  const totalGenerated = Number(customFields.total_generated || 0);
  const estimatedHours = formData.estimated_hours ?? workItem.estimated_hours ?? null;
  const assigneeName = String(customFields.assigned_to_name || formData.assigned_to_name || 'Unassigned');
  const escalationName = String(customFields.escalation_to_name || 'No escalation target');
  const contactPerson = String(customFields.contact_person_name || formData.contact_person_name || workItem.contact_person || 'Not specified');
  const tags = Array.isArray(workItem.tags) ? workItem.tags : [];
  const isPaused = Boolean(customFields.is_paused);
  const isActive = customFields.is_active !== false;
  const priorityLabel = formatSubtypeLabel(String(formData.priority || workItem.priority || 'medium'));

  // ---- Run Now / Test Now ----
  const [running, setRunning] = useState(false);
  const [testing, setTesting] = useState(false);
  const [previewResult, setPreviewResult] = useState<{
    warnings: string[];
    errors: string[];
    data: {
      would_generate_title: string;
      would_generate_status: string;
      would_generate_priority: string;
      would_generate_due_date: string;
      would_create_appointment: boolean;
      occurrence_number: number;
      upcoming_dates: string[];
      next_generation_date: string | null;
      total_generated: number;
      is_paused: boolean;
      is_active: boolean;
    } | null;
  } | null>(null);

  const handleRunNow = useCallback(() => {
    confirm({
      title: 'Generate Job Now',
      message: `This will immediately generate a new job from "${title}" (occurrence #${totalGenerated + 1}). Continue?`,
      confirmText: 'Generate',
      cancelText: 'Cancel',
      variant: 'success',
      onConfirm: async () => {
        setRunning(true);
        try {
          const token = authService.getTokens()?.access_token;
          if (!token) throw new Error('Not authenticated');

          const response = await fetch('/api/v1/work-items/recurring', {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ action: 'generate', id: scheduleId }),
          });

          const result = await response.json();
          if (!response.ok || !result.success) {
            throw new Error(result.error || 'Failed to generate job');
          }

          notify.success(result.message || 'Job generated successfully');
          onRefresh?.();
        } catch (err: unknown) {
          notify.error(err instanceof Error ? err.message : 'Failed to generate job');
        } finally {
          setRunning(false);
        }
      },
    });
  }, [scheduleId, title, totalGenerated, onRefresh]);

  const handleTestNow = useCallback(async () => {
    setTesting(true);
    setPreviewResult(null);
    try {
      const token = authService.getTokens()?.access_token;
      if (!token) throw new Error('Not authenticated');

      const response = await fetch('/api/v1/work-items/recurring', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ action: 'preview', id: scheduleId }),
      });

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || 'Failed to preview schedule');
      }

      setPreviewResult({
        warnings: result.warnings || [],
        errors: result.errors || [],
        data: result.data || null,
      });

      if (result.errors?.length > 0) {
        notify.warning(`Schedule has ${result.errors.length} issue(s) — see preview below`);
      } else if (result.warnings?.length > 0) {
        notify.info(`Schedule is valid with ${result.warnings.length} warning(s)`);
      } else {
        notify.success('Schedule is valid and ready to generate');
      }
    } catch (err: unknown) {
      notify.error(err instanceof Error ? err.message : 'Failed to test schedule');
    } finally {
      setTesting(false);
    }
  }, [scheduleId]);

  // ---- Shared input styles ----
  const inputCls = `w-full px-2.5 py-1.5 rounded-lg border text-sm ${
    isDark
      ? 'bg-gray-900/60 border-gray-600 text-white placeholder-gray-500 focus:border-violet-500'
      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-violet-500'
  } focus:outline-none focus:ring-1 focus:ring-violet-500/40 transition-colors`;

  const selectCls = inputCls;

  // ---- Editable row component ----
  function EditableRow({
    label,
    icon: Icon,
    children,
  }: {
    label: string;
    icon?: React.ElementType;
    children: React.ReactNode;
  }) {
    return (
      <div className="flex items-center justify-between gap-4 py-2.5">
        <div className={`flex items-center gap-2 text-sm flex-shrink-0 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
          {Icon ? <Icon className="w-4 h-4" /> : null}
          <span>{label}</span>
        </div>
        <div className="flex-1 max-w-[220px]">{children}</div>
      </div>
    );
  }

  // ---- Toggle component ----
  function Toggle({
    checked,
    onChange,
  }: {
    checked: boolean;
    onChange: (val: boolean) => void;
  }) {
    return (
      <button
        type="button"
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full transition-colors ${
          checked ? 'bg-violet-500' : isDark ? 'bg-gray-600' : 'bg-gray-300'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            checked ? 'translate-x-4' : 'translate-x-0.5'
          } mt-0.5`}
        />
      </button>
    );
  }

  return (
    <div className="space-y-4">
      {/* ---- Save / Reset bar ---- */}
      {isDirty && (
        <div className={`sticky top-0 z-10 flex items-center justify-between gap-3 rounded-xl border p-3 ${
          isDark ? 'bg-violet-900/20 border-violet-700' : 'bg-violet-50 border-violet-200'
        }`}>
          <span className={`text-sm font-medium ${isDark ? 'text-violet-300' : 'text-violet-700'}`}>
            Unsaved changes
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleReset}
              disabled={saving}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                isDark ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-200'
              }`}
            >
              <X className="w-4 h-4" />
              Reset
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-medium bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50 transition-colors"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      )}

      {/* ---- Header card ---- */}
      <div className={`rounded-xl border p-4 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-2xl bg-violet-100 text-violet-600 dark:bg-violet-900/30 dark:text-violet-300 flex items-center justify-center flex-shrink-0">
              <Repeat className="w-7 h-7" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{title}</h3>
                <StatusBadge isActive={isActive} isPaused={isPaused} />
              </div>
              <p className={`mt-1 text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{frequencyLabel}</p>
              {description ? (
                <p className={`mt-2 text-sm whitespace-pre-wrap ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{description}</p>
              ) : null}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 sm:min-w-[320px]">
            <InfoCard
              icon={Calendar}
              label="Next generation"
              value={formatDate(nextGenerationDate)}
              helper={editData.generate_time ? `${editData.generate_time} · ${editData.timezone}` : editData.timezone}
              accentClassName="text-violet-600 dark:text-violet-300"
              isDark={isDark}
            />
            <InfoCard
              icon={ClipboardList}
              label="Generated jobs"
              value={totalGenerated}
              helper={lastGeneratedAt ? `Last run ${formatDateTime(lastGeneratedAt)}` : 'No generated jobs yet'}
              accentClassName="text-blue-600 dark:text-blue-300"
              isDark={isDark}
            />
            <InfoCard
              icon={Timer}
              label="Due window"
              value={editData.due_days_after_creation != null ? `${editData.due_days_after_creation} day${Number(editData.due_days_after_creation) === 1 ? '' : 's'}` : '—'}
              helper={editData.overdue_days != null ? `Overdue after ${editData.overdue_days} day${Number(editData.overdue_days) === 1 ? '' : 's'}` : 'No overdue rule'}
              accentClassName="text-amber-600 dark:text-amber-300"
              isDark={isDark}
            />
            <InfoCard
              icon={ArrowUpRight}
              label="Escalation"
              value={editData.escalation_days != null ? `${editData.escalation_days} day${Number(editData.escalation_days) === 1 ? '' : 's'}` : 'Disabled'}
              helper={editData.escalation_days != null ? escalationName : 'No escalation target'}
              accentClassName="text-rose-600 dark:text-rose-300"
              isDark={isDark}
            />
          </div>
        </div>
      </div>

      {/* ---- Action bar: Run Now / Test Now ---- */}
      <div className={`flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between rounded-xl border p-3 ${
        isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      }`}>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleTestNow}
            disabled={testing || running}
            className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 ${
              isDark
                ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <FlaskConical className="w-4 h-4" />}
            {testing ? 'Testing...' : 'Test Now'}
          </button>
          <button
            type="button"
            onClick={handleRunNow}
            disabled={running || testing || isPaused}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50 transition-colors"
            title={isPaused ? 'Resume the schedule to generate jobs' : 'Generate a job from this schedule now'}
          >
            {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
            {running ? 'Generating...' : 'Run Now'}
          </button>
        </div>
        <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
          {isPaused ? 'Schedule is paused — resume to enable Run Now' : 'Test validates the schedule without creating a job. Run generates immediately.'}
        </p>
      </div>

      {/* ---- Test preview results ---- */}
      {previewResult && (
        <div className={`rounded-xl border p-4 space-y-3 ${
          isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <div className="flex items-center justify-between">
            <h4 className={`text-sm font-semibold flex items-center gap-2 ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
              <FlaskConical className="w-4 h-4 text-violet-500" />
              Test Results
            </h4>
            <button
              type="button"
              onClick={() => setPreviewResult(null)}
              className={`p-1 rounded-lg ${isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {previewResult.errors.length > 0 && (
            <div className={`rounded-lg p-3 space-y-1 ${isDark ? 'bg-red-900/20 border border-red-800' : 'bg-red-50 border border-red-200'}`}>
              {previewResult.errors.map((err, i) => (
                <div key={i} className="flex items-start gap-2 text-sm text-red-600 dark:text-red-400">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>{err}</span>
                </div>
              ))}
            </div>
          )}

          {previewResult.warnings.length > 0 && (
            <div className={`rounded-lg p-3 space-y-1 ${isDark ? 'bg-amber-900/20 border border-amber-800' : 'bg-amber-50 border border-amber-200'}`}>
              {previewResult.warnings.map((warn, i) => (
                <div key={i} className="flex items-start gap-2 text-sm text-amber-600 dark:text-amber-400">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>{warn}</span>
                </div>
              ))}
            </div>
          )}

          {previewResult.errors.length === 0 && previewResult.data && (
            <div className={`rounded-lg p-3 space-y-2.5 ${isDark ? 'bg-gray-900/40' : 'bg-gray-50'}`}>
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                <span className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                  Would generate: {previewResult.data.would_generate_title}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-3 text-xs sm:grid-cols-3">
                <div>
                  <p className={isDark ? 'text-gray-500' : 'text-gray-500'}>Status</p>
                  <p className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    {previewResult.data.would_generate_status === 'open' ? 'Open (auto-approved)' : 'Draft (needs approval)'}
                  </p>
                </div>
                <div>
                  <p className={isDark ? 'text-gray-500' : 'text-gray-500'}>Priority</p>
                  <p className={`font-medium capitalize ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    {previewResult.data.would_generate_priority}
                  </p>
                </div>
                <div>
                  <p className={isDark ? 'text-gray-500' : 'text-gray-500'}>Due date</p>
                  <p className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    {formatDate(previewResult.data.would_generate_due_date)}
                  </p>
                </div>
                <div>
                  <p className={isDark ? 'text-gray-500' : 'text-gray-500'}>Appointment</p>
                  <p className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    {previewResult.data.would_create_appointment ? 'Yes' : 'No'}
                  </p>
                </div>
                <div>
                  <p className={isDark ? 'text-gray-500' : 'text-gray-500'}>Occurrence</p>
                  <p className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    #{previewResult.data.occurrence_number} (of {previewResult.data.total_generated + 1} generated)
                  </p>
                </div>
                <div>
                  <p className={isDark ? 'text-gray-500' : 'text-gray-500'}>Next scheduled</p>
                  <p className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    {formatDate(previewResult.data.next_generation_date)}
                  </p>
                </div>
              </div>
              {previewResult.data.upcoming_dates.length > 0 && (
                <div>
                  <p className={`text-xs mb-1.5 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Upcoming generation dates</p>
                  <div className="flex flex-wrap gap-2">
                    {previewResult.data.upcoming_dates.map((date, i) => (
                      <span
                        key={i}
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs ${isDark ? 'bg-gray-800 text-gray-300' : 'bg-white text-gray-700 border border-gray-200'}`}
                      >
                        <Calendar className="w-3 h-3" />
                        {formatDate(date)}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ---- Schedule Details (editable) + Customer (read-only) ---- */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className={`rounded-xl border p-4 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <h4 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
            <Calendar className="w-4 h-4 text-violet-500" />
            Schedule Details
          </h4>
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            <DetailRow label="Reference" value={workItem.item_number} icon={Repeat} isDark={isDark} />
            <EditableRow label="Frequency" icon={Clock}>
              <select
                className={selectCls}
                value={editData.frequency || 'monthly'}
                onChange={e => handleChange('frequency', e.target.value)}
              >
                {FREQUENCY_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </EditableRow>
            <EditableRow label="Interval" icon={Repeat}>
              <input
                type="number"
                min={1}
                max={365}
                className={inputCls}
                value={editData.interval_count ?? 1}
                onChange={e => handleChange('interval_count', e.target.value)}
              />
            </EditableRow>
            {editData.frequency === 'weekly' && (
              <EditableRow label="Day of week" icon={Calendar}>
                <select
                  className={selectCls}
                  value={editData.day_of_week ?? ''}
                  onChange={e => handleChange('day_of_week', e.target.value === '' ? '' : Number(e.target.value))}
                >
                  <option value="">Auto</option>
                  {DAYS_OF_WEEK.map((day, idx) => (
                    <option key={idx} value={idx}>{day}</option>
                  ))}
                </select>
              </EditableRow>
            )}
            {['monthly', 'quarterly', 'biannually', 'annually'].includes(editData.frequency) && (
              <EditableRow label="Day of month" icon={Calendar}>
                <input
                  type="number"
                  min={1}
                  max={31}
                  className={inputCls}
                  value={editData.day_of_month ?? ''}
                  placeholder="Auto"
                  onChange={e => handleChange('day_of_month', e.target.value === '' ? '' : Number(e.target.value))}
                />
              </EditableRow>
            )}
            {editData.frequency === 'annually' && (
              <EditableRow label="Month of year" icon={Calendar}>
                <select
                  className={selectCls}
                  value={editData.month_of_year ?? ''}
                  onChange={e => handleChange('month_of_year', e.target.value === '' ? '' : Number(e.target.value))}
                >
                  <option value="">Auto</option>
                  {MONTHS.map((month, idx) => (
                    <option key={idx} value={idx + 1}>{month}</option>
                  ))}
                </select>
              </EditableRow>
            )}
            <EditableRow label="Start date" icon={Calendar}>
              <input
                type="date"
                className={inputCls}
                value={editData.start_date || ''}
                onChange={e => handleChange('start_date', e.target.value)}
              />
            </EditableRow>
            <EditableRow label="End date" icon={Calendar}>
              <input
                type="date"
                className={inputCls}
                value={editData.end_date || ''}
                onChange={e => handleChange('end_date', e.target.value)}
              />
            </EditableRow>
            <EditableRow label="End after" icon={Activity}>
              <input
                type="number"
                min={1}
                className={inputCls}
                value={editData.end_after_occurrences ?? ''}
                placeholder="No limit"
                onChange={e => handleChange('end_after_occurrences', e.target.value === '' ? '' : Number(e.target.value))}
              />
            </EditableRow>
            <EditableRow label="Generate at" icon={Clock}>
              <input
                type="time"
                className={inputCls}
                value={editData.generate_time || '06:00'}
                onChange={e => handleChange('generate_time', e.target.value)}
              />
            </EditableRow>
            <EditableRow label="Timezone" icon={Clock}>
              <select
                className={selectCls}
                value={editData.timezone || 'Africa/Johannesburg'}
                onChange={e => handleChange('timezone', e.target.value)}
              >
                {TIMEZONE_OPTIONS.map(tz => (
                  <option key={tz} value={tz}>{tz}</option>
                ))}
              </select>
            </EditableRow>
          </div>
        </div>

        <div className={`rounded-xl border p-4 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <h4 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
            <Users className="w-4 h-4 text-blue-500" />
            Customer & Ownership
          </h4>
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            <DetailRow label="Customer" value={workItem.contact_name || 'Not specified'} icon={Briefcase} isDark={isDark} />
            <DetailRow label="Contact email" value={workItem.contact_email || 'Not specified'} icon={User} isDark={isDark} />
            <DetailRow label="Location" value={workItem.location_address || workItem.location_name || 'Not specified'} icon={MapPin} isDark={isDark} />
            <DetailRow label="Contact person" value={contactPerson} icon={User} isDark={isDark} />
            <DetailRow label="Assigned to" value={assigneeName} icon={Users} isDark={isDark} />
            <DetailRow label="Escalate to" value={escalationName} icon={ArrowUpRight} isDark={isDark} />
          </div>
        </div>
      </div>

      {/* ---- Rules & Automation (editable) + Notes (editable) ---- */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className={`rounded-xl border p-4 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <h4 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
            <Shield className="w-4 h-4 text-emerald-500" />
            Rules & Automation
          </h4>
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            <DetailRow label="Priority" value={priorityLabel} icon={AlertTriangle} isDark={isDark} />
            <DetailRow label="Estimated hours" value={estimatedHours != null && estimatedHours !== '' ? `${estimatedHours} hrs` : 'Not set'} icon={Timer} isDark={isDark} />
            <EditableRow label="Due days" icon={Timer}>
              <input
                type="number"
                min={0}
                className={inputCls}
                value={editData.due_days_after_creation ?? 7}
                onChange={e => handleChange('due_days_after_creation', Number(e.target.value))}
              />
            </EditableRow>
            <EditableRow label="Overdue days" icon={AlertTriangle}>
              <input
                type="number"
                min={0}
                className={inputCls}
                value={editData.overdue_days ?? 3}
                onChange={e => handleChange('overdue_days', Number(e.target.value))}
              />
            </EditableRow>
            <EditableRow label="Escalation days" icon={ArrowUpRight}>
              <input
                type="number"
                min={0}
                className={inputCls}
                value={editData.escalation_days ?? 5}
                onChange={e => handleChange('escalation_days', Number(e.target.value))}
              />
            </EditableRow>
            <div className="flex items-center justify-between gap-4 py-2.5">
              <div className={`flex items-center gap-2 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                <Users className="w-4 h-4" />
                <span>Auto-assign</span>
              </div>
              <Toggle checked={Boolean(editData.auto_assign)} onChange={val => handleChange('auto_assign', val)} />
            </div>
            <div className="flex items-center justify-between gap-4 py-2.5">
              <div className={`flex items-center gap-2 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                <CheckCircle2 className="w-4 h-4" />
                <span>Auto-approve</span>
              </div>
              <Toggle checked={Boolean(editData.auto_approve)} onChange={val => handleChange('auto_approve', val)} />
            </div>
            <div className="flex items-center justify-between gap-4 py-2.5">
              <div className={`flex items-center gap-2 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                <Calendar className="w-4 h-4" />
                <span>Create appointments</span>
              </div>
              <Toggle checked={Boolean(editData.create_appointments)} onChange={val => handleChange('create_appointments', val)} />
            </div>
            <DetailRow label="Schedule state" value={isPaused ? 'Paused' : isActive ? 'Active' : 'Inactive'} icon={isPaused ? PauseCircle : PlayCircle} isDark={isDark} />
          </div>
        </div>

        <div className={`rounded-xl border p-4 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <h4 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
            <ClipboardList className="w-4 h-4 text-indigo-500" />
            Notes & Labels
          </h4>
          <div className="space-y-4">
            <div>
              <p className={`text-xs uppercase tracking-wide mb-1 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Operational Notes</p>
              <textarea
                className={`${inputCls} min-h-[88px] resize-y`}
                value={editData.notes || ''}
                placeholder="No operational notes recorded for this recurring schedule."
                onChange={e => handleChange('notes', e.target.value)}
              />
            </div>
            <div>
              <p className={`text-xs uppercase tracking-wide mb-1 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Internal Notes</p>
              <textarea
                className={`${inputCls} min-h-[88px] resize-y`}
                value={editData.internal_notes || ''}
                placeholder="No internal notes recorded."
                onChange={e => handleChange('internal_notes', e.target.value)}
              />
            </div>
            <div>
              <p className={`text-xs uppercase tracking-wide mb-2 flex items-center gap-1 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                <Tags className="w-3.5 h-3.5" />
                Tags
              </p>
              <div className="flex flex-wrap gap-2">
                {tags.length > 0 ? tags.map((tag) => (
                  <span
                    key={tag}
                    className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${isDark ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-700'}`}
                  >
                    {tag}
                  </span>
                )) : (
                  <span className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>No tags assigned.</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ---- Bottom Save bar (always visible when dirty) ---- */}
      {isDirty && (
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={handleReset}
            disabled={saving}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              isDark ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-200'
            }`}
          >
            <X className="w-4 h-4" />
            Reset
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-medium bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50 transition-colors"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      )}
    </div>
  );
}